import { CUSTOM_FIELD_TYPES, CustomFieldCreateParams, CustomFieldParams } from "./customFieldTypes";
import { ContainerInterface } from "./containerTypes";
import { PendingValidation } from "./validationTypes";
import { ItemReferenceParams } from "./itemReferenceTypes";
import { OperationInterface } from "./operationTypes";
import { FusaReply } from "./replyTypes";
import { EquipmentItemData } from "./equipmentTypes";

export enum ParameterType {
  INPUT = 'input',
  OUTPUT = 'output',
  FIELD = 'field',
  XSLIDER = 'xSlider',
  YSLIDER = 'ySlider',
  INTRODUCTION = 'introduction',
  CONFIG = 'config',
  TEST_INPUT = 'testInput',
}

export enum ParameterSubjectType {
  STEP = 'step',
  OPERATION = 'operation',
  PROCEDURE = 'procedure',
  VALIDATION = 'validation',
  INTERFACE_FUNCTION = 'interfaceFunction',
  CONTEXT_LOG = 'contextLog',
  PERIPHERAL_FUNCTION = 'peripheralFunction',
  REQUIREMENT = 'requirement',
  PART = 'part',
  IMPLEMENTATION = 'implementation',
}

export enum ParameterInputType {
  FREE_FORM = 'freeForm',
  SINGLE_CHOICE = 'singleChoice',
  MULTIPLE_CHOICE = 'multipleChoice',
  LIST = 'list',
  TABLE = 'table',
}

export const MULTIPLE_VALUES_INPUT_TYPES = [ParameterInputType.MULTIPLE_CHOICE, ParameterInputType.LIST, ParameterInputType.TABLE];
export const CHOICE_INPUT_TYPES = [ParameterInputType.SINGLE_CHOICE, ParameterInputType.MULTIPLE_CHOICE];
export const SINGLE_VALUE_INPUT_TYPES = [ParameterInputType.FREE_FORM, ParameterInputType.SINGLE_CHOICE];

export interface ParameterCreateParams {
  type: ParameterType;
  title?: string;
  subjectId: string;
  subjectType: ParameterSubjectType;
  identifier?: string | null;
  position: number;
  fields: Omit<CustomFieldCreateParams, 'subjectId' | 'subjectType'>[];
  sourceId?: string;
  inputType: ParameterInputType;
  viewOnly?: boolean;
  description?: string;
  allowCustomChoices: boolean;
}

export interface ParameterValidateParams extends ParameterCreateParams {
  id: string;
  createdAt?: Date | string;
  updatedAt?: Date | string;
}

export interface ParameterParams extends ParameterCreateParams {
  id: string;
  /** The org that owns the parameter. ParameterAccess writes it from the writer org; callers never choose it. */
  propelOrgId?: string;
  createdAt?: Date | string;
  updatedAt?: Date | string;
  fields: CustomFieldParams[];
  identifier?: string;
  inputType: ParameterInputType;
}

export interface ParameterCreateRequest {
  type: ParameterType;
  subjectId: string;
  subjectType: ParameterSubjectType;
  title: string;
  position: number;
  fieldType: CUSTOM_FIELD_TYPES;
  inputType: ParameterInputType;
  identifier?: string;
  procedureId?: string;
  description?: string;
  fileName?: string;
}

export type ParameterInterface = {
  id: string;
  createdAt?: Date | string;
  updatedAt?: Date | string;
  type: ParameterType;
  title: string;
  subjectId: string;
  subjectType: ParameterSubjectType;
  identifier: string;
  position: number;
  fields: CustomFieldParams[];
  sourceId: string;
  inputType: ParameterInputType;
  viewOnly: boolean;
  parentId: string;
  fieldType: CUSTOM_FIELD_TYPES;
  allowCustomChoices: boolean;
};

export type SelectableParameter = {
  id: string;
  title: string;
  fieldType: CUSTOM_FIELD_TYPES;
  inputType: ParameterInputType;
  keyId?: string;
  subjectType: ParameterSubjectType;
  type: ParameterType;
  subjectId: string;
};

export interface ParameterReply extends FusaReply {
  parameter?: ParameterInterface;
  uploadUrl?: string;
}

export interface ParameterDeleteRequest {
  id: string;
}

export interface ParameterReadAllReply extends FusaReply {
  parameters?: ParameterParams[];
}

export interface ParameterUpdateRequest {
  parameter: ParameterParams;
  procedureId?: string;
  hideToast?: boolean;
};

export interface ParameterReadAllBySubjectRequest {
  subjectId: string;
  subjectType: ParameterSubjectType;
}

export interface UpdatedParamsAndRefs {
  updatedParameters: ParameterParams[];
  updatedReferences: ItemReferenceParams[];
  updatedEquipment?: EquipmentItemData[];
  /**
   * The validations this write made stale, for the page to have the desktop
   * app run. Set by `ValidationRunner.withPendingValidations`; on the shared
   * shape rather than on each reply so that every endpoint which cascades can
   * answer the same way, and `handleUpdatedParamsAndRefs` can act on it
   * wherever a cascade's answer arrives.
   */
  pendingValidations?: PendingValidation[];
}

export interface ParameterUpdateReply extends FusaReply {
  updatedParameters: ParameterParams[];
  updatedOperations: OperationInterface[];
  updatedContainers: ContainerInterface[];
}

export interface SelectFieldReply extends UpdatedParamsAndRefs, FusaReply {} 
